import { useCallback, useMemo } from "react";
import { useOptInInfluencersWhatsapp } from "../../hooks/usePlanSummary";
import { shareUrlFor, useSendApprovalWhatsapp } from "../../hooks/useApprovals";
import WhatsAppTemplateSendDialog from "../whatsapp/WhatsAppTemplateSendDialog";

const CREATOR_LINK_TOKEN = "{{creatorLink}}";
const SENDER_KEY = "waScriptSenderName";
const SCRIPT_REVIEW_TEMPLATE = "script_review_ready";

// Tokens the server fills per creator (routes/approvals.js fillSendTokens) — offered as chips under
// the variable boxes. The sender isn't a token: whoever is sending types it.
const TOKENS = [
  { token: "{{creatorName}}", label: "Creator name" },
  { token: "{{brandName}}", label: "Brand" },
  { token: "{{planName}}", label: "Plan" },
  { token: CREATOR_LINK_TOKEN, label: "Creator link" },
];

// Pre-filled layout for the script-review template: {{1}} creator, {{2}} sender (typed, remembered
// in this browser), {{3}} brand, {{4}} plan, {{5}} link.
const SCRIPT_REVIEW_LAYOUT = ["{{creatorName}}", "", "{{brandName}}", "{{planName}}", CREATOR_LINK_TOKEN];
const SENDER_INDEX = 1;

const readSender = () => {
  try {
    return localStorage.getItem(SENDER_KEY) || "";
  } catch {
    return "";
  }
};
const saveSender = (v) => {
  try {
    localStorage.setItem(SENDER_KEY, v);
  } catch {
    /* storage unavailable — the prefill just won't persist */
  }
};

const isScriptReviewLayout = (template, count) => template?.name === SCRIPT_REVIEW_TEMPLATE && count === SCRIPT_REVIEW_LAYOUT.length;

// Script / Video board bulk send: the shared dialog, plus per-creator tokens. `selectedItems` are
// board rows (id + planInfluencerId); the server swaps each token for that creator's own value.
function ApprovalWhatsAppSendModal({ open, onClose, kind, planId, planName, brandName, selectedItems }) {
  const sendMutation = useSendApprovalWhatsapp(kind, planId);
  const optInMutation = useOptInInfluencersWhatsapp(planId);
  const recipientIds = useMemo(() => selectedItems.map((s) => s.id), [selectedItems]);

  // Preview-only stand-ins: the first selected creator's name, the real brand / plan, and this
  // board's link shape — the server does the real substitution at send time.
  const toPreviewVariables = useCallback(
    (variables) => {
      const creator = selectedItems[0]?.name || selectedItems[0]?.username || "Creator";
      const sample = {
        "{{creatorName}}": creator,
        "{{name}}": creator,
        "{{brandName}}": brandName || planName || "Brand",
        "{{planName}}": planName || "Plan",
        [CREATOR_LINK_TOKEN]: shareUrlFor(kind, "<creator-token>"),
      };
      return variables.map((v) => Object.entries(sample).reduce((acc, [t, val]) => acc.split(t).join(val), v || ""));
    },
    [selectedItems, brandName, planName, kind]
  );

  const initialVariables = (template, count) => {
    if (!isScriptReviewLayout(template, count)) return null;
    const next = [...SCRIPT_REVIEW_LAYOUT];
    next[SENDER_INDEX] = readSender();
    return next;
  };

  const rememberSender = (i, value, template) => {
    if (i === SENDER_INDEX && isScriptReviewLayout(template, SCRIPT_REVIEW_LAYOUT.length)) saveSender(value);
  };

  return (
    <WhatsAppTemplateSendDialog
      open={open}
      onClose={onClose}
      planName={planName}
      recipientIds={recipientIds}
      noun="creator"
      onOptIn={() => optInMutation.mutateAsync(selectedItems.map((s) => s.planInfluencerId))}
      sendChunk={(itemIds, body) => sendMutation.mutateAsync({ itemIds, ...body })}
      initialVariables={initialVariables}
      onVariableChange={rememberSender}
      tokens={TOKENS}
      toPreviewVariables={toPreviewVariables}
    />
  );
}

export default ApprovalWhatsAppSendModal;
